
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Trash2, Edit2, Check, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AlertRule } from '@/types/alertSettings';

interface AlertRuleCardProps {
  rule: AlertRule;
  type: 'absence' | 'late';
  onUpdate: (rule: AlertRule) => void;
  onDelete: (ruleId: string) => void;
}

export const AlertRuleCard = ({ rule, type, onUpdate, onDelete }: AlertRuleCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedRule, setEditedRule] = useState(rule);

  // تحديث editedRule عند تغيير rule الخارجي
  useEffect(() => {
    setEditedRule(rule);
  }, [rule]);

  const handleSave = () => {
    onUpdate(editedRule);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedRule(rule);
    setIsEditing(false);
  };

  const handleToggle = (enabled: boolean) => {
    const updatedRule = { ...rule, enabled };
    setEditedRule(updatedRule);
    onUpdate(updatedRule);
  };

  const typeStyles = type === 'absence' 
    ? {
        bgColor: 'bg-destructive/10',
        borderColor: 'border-destructive/30',
        textColor: 'text-destructive',
        hoverBg: 'hover:bg-destructive/20'
      }
    : {
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/30', 
        textColor: 'text-warning',
        hoverBg: 'hover:bg-warning/20'
      };

  return (
    <Card className={`${typeStyles.bgColor} ${typeStyles.borderColor} border-2`} dir="rtl">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(rule.id)}
                  className="h-8 w-8 p-0 hover:bg-destructive/20 text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  className={`h-8 w-8 p-0 ${typeStyles.hoverBg} ${typeStyles.textColor}`}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancel}
                  className="h-8 w-8 p-0 hover:bg-destructive/20 text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleSave}
                  className="h-8 w-8 p-0 hover:bg-secondary/20 text-secondary"
                >
                  <Check className="h-4 w-4" />
                </Button>
              </>
            )}
            <Switch
              checked={rule.enabled}
              onCheckedChange={handleToggle}
              className="data-[state=checked]:bg-secondary"
            />
          </div>
          <CardTitle className={`text-sm ${typeStyles.textColor} text-right`}>
            {type === 'absence' ? 'تنبيه غياب' : 'تنبيه تأخر'} - {rule.threshold} {type === 'absence' ? 'أيام' : 'مرات'}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-right pt-6">
        {isEditing ? (
          <>
            <div>
              <Label className={`${typeStyles.textColor} block text-right mb-1`}>
                {type === 'absence' ? 'عدد الأيام' : 'عدد المرات'}
              </Label>
              <Input
                type="number"
                min="1"
                max={type === 'absence' ? "30" : "20"}
                value={editedRule.threshold}
                onChange={(e) => setEditedRule({
                  ...editedRule,
                  threshold: parseInt(e.target.value) || 1
                })}
                className="bg-white/70 text-right"
                dir="rtl"
              />
            </div>
            <div>
              <Label className={`${typeStyles.textColor} block text-right mb-1`}>عنوان التنبيه</Label>
              <Input
                value={editedRule.title}
                onChange={(e) => setEditedRule({
                  ...editedRule,
                  title: e.target.value
                })}
                className="bg-white/70 text-right"
                dir="rtl"
              />
            </div>
            <div>
              <Label className={`${typeStyles.textColor} block text-right mb-1`}>وصف التنبيه</Label>
              <Textarea
                value={editedRule.description}
                onChange={(e) => setEditedRule({
                  ...editedRule,
                  description: e.target.value
                })}
                className="bg-white/70 text-right"
                rows={2}
                dir="rtl"
              />
            </div>
          </>
        ) : (
          <div className="space-y-2 text-right">
            <div>
              <p className={`font-medium ${typeStyles.textColor} text-right`}>{rule.title}</p>
              <p className={`text-sm ${typeStyles.textColor}/80 text-right`}>{rule.description}</p>
            </div>
            <div className={`text-xs ${typeStyles.textColor}/60 text-right`}>
              الحالة: {rule.enabled ? 'مفعل' : 'معطل'}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
